import { useCallback, useEffect } from 'react'
import { useBlocker } from 'react-router-dom'
import { Dialog } from '../components/Dialog'

/**
 * Hook to prevent navigation when a form has unsaved changes.
 *
 * Displays a confirmation dialog when the user attempts to navigate away from a page
 * with unsaved changes. This works for both in-app navigation (via React Router)
 * and browser navigation events (refresh, close tab, back/forward buttons).
 *
 * @param isDirty - Boolean indicating whether the form has unsaved changes.
 *                  Typically derived from React Hook Form's `formState.isDirty`
 *                  or custom state tracking.
 *
 * @returns Object containing:
 *   - `isBlocked`: Boolean indicating if navigation is currently blocked.
 *   - `confirmLeave`: Callback to allow the blocked navigation to proceed.
 *   - `cancelLeave`: Callback to cancel the blocked navigation and stay on the page.
 *
 * @example
 * ```tsx
 * function MyForm() {
 *   const { formState: { isDirty }, handleSubmit, reset } = useForm();
 *   const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty);
 *
 *   return (
 *     <form onSubmit={handleSubmit(onSubmit)}>
 *       {/* form fields *\/}
 *       <UnsavedChangesDialog
 *         isBlocked={isBlocked}
 *         confirmLeave={confirmLeave}
 *         cancelLeave={cancelLeave}
 *       />
 *     </form>
 *   );
 * }
 * ```
 */
export function useUnsavedChangesWarning(isDirty: boolean) {
	const blocker = useBlocker(isDirty)

	useEffect(() => {
		if (!isDirty) return

		const handler = (e: BeforeUnloadEvent) => {
			e.preventDefault()
		}

		window.addEventListener('beforeunload', handler)
		return () => window.removeEventListener('beforeunload', handler)
	}, [isDirty])

	return {
		isBlocked: blocker.state === 'blocked',
		confirmLeave: useCallback(() => {
			if (blocker.state === 'blocked') blocker.proceed()
		}, [blocker]),
		cancelLeave: useCallback(() => {
			if (blocker.state === 'blocked') blocker.reset()
		}, [blocker]),
	}
}

/**
 * Companion component for `useUnsavedChangesWarning` that renders the
 * standard unsaved-changes confirmation dialog.
 */
export function UnsavedChangesDialog({
	isBlocked,
	confirmLeave,
	cancelLeave,
}: {
	isBlocked: boolean
	confirmLeave: () => void
	cancelLeave: () => void
}) {
	return (
		<Dialog
			isOpen={isBlocked}
			onClose={cancelLeave}
			onConfirm={confirmLeave}
			title='Unsaved Changes'
			message='You have unsaved changes that will be lost if you leave this page. Are you sure you want to continue?'
			type='confirm'
			confirmLabel='Leave'
		/>
	)
}
